import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Box,
  Button,
  Alert,
  Text,
  Divider,
  Tabs,
  TextInput,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconFileUpload,
  IconCheck,
  IconPlus,
  IconLink,
  IconBrandPinterest,
} from '@tabler/icons-react';
import { useAuth } from '../../../hooks/useAuth';
import { useAppUser } from '../../../hooks/useAppUser';
import { notifications } from '@mantine/notifications';
import { ImageUpload } from './ImageUpload';
import { PinterestImport } from './PinterestImport';
import { handleUsageLimitError } from '../../../utils/usageLimitNotifications';

interface ImportRecipeModalProps {
  opened: boolean;
  onClose: () => void;
  onImportStarted: () => void;
}

interface RecipeGroup {
  id: string;
  images: File[];
}

interface ImportState {
  activeTab: 'images' | 'url' | 'pinterest';
  recipeGroups: RecipeGroup[];
  urls: string[];
  error: string | null;
  importing: boolean;
}

export const ImportRecipeModal: React.FC<ImportRecipeModalProps> = ({
  opened,
  onClose,
  onImportStarted,
}) => {
  const [state, setState] = useState<ImportState>({
    activeTab: 'images',
    recipeGroups: [{ id: '1', images: [] }], // Start with one recipe group
    urls: [''], // Start with one empty URL
    error: null,
    importing: false,
  });
  const { user } = useAuth();
  const { user: appUser } = useAppUser();
  const pinterestConnected = appUser?.familyProfile?.pinterestConnected ?? false;

  // Handle Pinterest OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pinterest') === 'connected') {
      setState(prev => ({ ...prev, activeTab: 'pinterest' }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleImagesSelected = useCallback((recipeId: string, images: File[]) => {
    setState(prev => ({
      ...prev,
      recipeGroups: prev.recipeGroups.map(group => 
        group.id === recipeId ? { ...group, images } : group
      ),
      error: null,
    }));
  }, []);

  const handleImagesCleared = useCallback((recipeId: string) => {
    setState(prev => ({
      ...prev,
      recipeGroups: prev.recipeGroups.map(group => 
        group.id === recipeId ? { ...group, images: [] } : group
      ),
      error: null,
    }));
  }, []);

  const addRecipeGroup = useCallback(() => {
    const newId = Date.now().toString();
    setState(prev => ({
      ...prev,
      recipeGroups: [...prev.recipeGroups, { id: newId, images: [] }],
    }));
  }, []);

  const removeRecipeGroup = useCallback((recipeId: string) => {
    setState(prev => ({
      ...prev,
      recipeGroups: prev.recipeGroups.filter(group => group.id !== recipeId),
    }));
  }, []);

  const handleUrlChange = useCallback((index: number, url: string) => {
    setState(prev => ({
      ...prev,
      urls: prev.urls.map((u, i) => i === index ? url : u),
      error: null,
    }));
  }, []);

  const addUrl = useCallback(() => {
    setState(prev => ({
      ...prev,
      urls: [...prev.urls, ''],
    }));
  }, []);

  const removeUrl = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      urls: prev.urls.filter((_, i) => i !== index),
    }));
  }, []);

  const handleReset = useCallback(() => {
    setState({
      activeTab: 'images',
      recipeGroups: [{ id: '1', images: [] }],
      urls: [''],
      error: null,
      importing: false,
    });
  }, []);

  const handleStartImport = useCallback(async () => {
    if (!user) return;

    let importPromises: Promise<any>[] = [];

    if (state.activeTab === 'images') {
      // Validate that each recipe group has at least one image
      const recipesWithImages = state.recipeGroups.filter(group => group.images.length > 0);
      
      if (recipesWithImages.length === 0) {
        notifications.show({
          title: 'No files selected',
          message: 'Please select at least one image or PDF for each recipe to import',
          color: 'orange',
          icon: <IconAlertCircle size={18} />,
          autoClose: 7000,
        });
        return;
      }

      const token = await user.getIdToken();

      // Submit each recipe group as a separate image import request
      recipesWithImages.forEach((group) => {
        const formData = new FormData();
        group.images.forEach((image) => {
          formData.append('images', image);
        });

        const importPromise = fetch(`${process.env.REACT_APP_API_URL}/api/recipes/import/images`, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }).then(async response => {
          if (!response.ok) {
            const data = await response.json();
            
            // Handle 429 usage limit errors with user-friendly notifications
            if (handleUsageLimitError(response, data)) {
              throw new Error('Usage limit exceeded');
            }
            
            // Handle duplicate errors with better messaging
            if (response.status === 409 && data.isDuplicate) {
              const errorMessage = data.error || 'This recipe has already been imported';
              const friendlyMessage = errorMessage.includes('images') 
                ? 'You\'ve already imported a recipe using one or more of these files. Check your cookbook to find the existing recipe.'
                : 'You\'ve already imported this recipe. Check your cookbook to find it.';
              throw new Error(friendlyMessage);
            }
            
            throw new Error(data.error || 'Failed to import recipe');
          }
          return response.json();
        });

        importPromises.push(importPromise);
      });
    } else if (state.activeTab === 'url') {
      // Validate that we have valid URLs, auto-prepending https:// if needed
      const validUrls = state.urls
        .filter(url => url.trim() !== '')
        .map(url => {
          const trimmed = url.trim();
          return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        });
      
      if (validUrls.length === 0) {
        notifications.show({
          title: 'No URLs provided',
          message: 'Please enter at least one URL to import from',
          color: 'orange',
          icon: <IconAlertCircle size={18} />,
          autoClose: 7000,
        });
        return;
      }

      // Validate URL formats
      const invalidUrls = validUrls.filter(url => {
        try {
          new URL(url.trim());
          return false;
        } catch {
          return true;
        }
      });

      if (invalidUrls.length > 0) {
        notifications.show({
          title: 'Invalid URLs',
          message: 'Please check that all URLs are valid web addresses',
          color: 'orange',
          icon: <IconAlertCircle size={18} />,
          autoClose: 7000,
        });
        return;
      }


      const token = await user.getIdToken();

      // Submit each URL as a separate import request
      validUrls.forEach((url) => {
        const importPromise = fetch(`${process.env.REACT_APP_API_URL}/api/recipes/import/url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url: url.trim() }),
        }).then(async response => {
          if (!response.ok) {
            const data = await response.json();
            
            // Handle 429 usage limit errors with user-friendly notifications
            if (handleUsageLimitError(response, data)) {
              throw new Error('Usage limit exceeded');
            }
            
            // Handle duplicate errors with better messaging
            if (response.status === 409 && data.isDuplicate) {
              const errorMessage = data.error || 'This recipe has already been imported';
              const friendlyMessage = errorMessage.includes('images') 
                ? 'You\'ve already imported a recipe using one or more of these files. Check your cookbook to find the existing recipe.'
                : 'You\'ve already imported this recipe. Check your cookbook to find it.';
              throw new Error(friendlyMessage);
            }
            
            throw new Error(data.error || 'Failed to import recipe from URL');
          }
          return response.json();
        });

        importPromises.push(importPromise);
      });
    }

    setState(prev => ({ ...prev, importing: true, error: null }));

    try {
      // Wait for all imports to complete and collect results
      const results = await Promise.allSettled(importPromises);
      
      // Separate successes from failures
      const successes = results.filter(result => result.status === 'fulfilled');
      const failures = results.filter(result => result.status === 'rejected') as PromiseRejectedResult[];
      
      const successCount = successes.length;
      const failureCount = failures.length;
      
      if (failureCount === 0) {
        // All imports succeeded
        notifications.show({
          title: `${successCount} recipe import${successCount > 1 ? 's' : ''} started`,
          message: `Your recipe${successCount > 1 ? 's are' : ' is'} being processed. ${successCount > 1 ? 'They' : 'It'} will appear in your cookbook when ready.`,
          color: 'blue',
          icon: <IconCheck size={18} />,
          autoClose: 6000,
        });

        // Close modal and reset state
        onClose();
        onImportStarted();
        handleReset();
        
      } else if (successCount === 0) {
        // All imports failed
        const firstError = failures[0].reason.message || 'Failed to import recipes';
        const isUsageLimitError = failures.some(failure => failure.reason.message === 'Usage limit exceeded');
        
        setState(prev => ({
          ...prev,
          importing: false,
          error: failureCount === 1 ? firstError : `All ${failureCount} imports failed. First error: ${firstError}`,
        }));

        // Only show generic error notification if it's not a usage limit error
        // (usage limit errors already show a specific notification)
        if (!isUsageLimitError) {
          notifications.show({
            title: 'Import failed',
            message: failureCount === 1 ? firstError : `All ${failureCount} recipe imports failed.`,
            color: 'red',
            icon: <IconAlertCircle size={18} />,
            autoClose: 10000,
          });
        }
        
      } else {
        // Partial success - some succeeded, some failed
        setState(prev => ({
          ...prev,
          importing: false,
          error: null,
        }));

        // Group failures by error type for better messaging
        const duplicateErrors = failures.filter(f => 
          f.reason.message?.includes('already imported') || 
          f.reason.message?.includes('already been used')
        );
        const otherErrors = failures.filter(f => 
          !f.reason.message?.includes('already imported') && 
          !f.reason.message?.includes('already been used') &&
          f.reason.message !== 'Usage limit exceeded'
        );

        // Calculate non-usage-limit failures for the message
        const nonUsageLimitFailures = duplicateErrors.length + otherErrors.length;
        
        let failureMessage = '';
        if (nonUsageLimitFailures === 0) {
          // Only usage limit errors (already shown specific notification)
          failureMessage = '';
        } else if (duplicateErrors.length > 0 && otherErrors.length === 0) {
          failureMessage = `${duplicateErrors.length} recipe${duplicateErrors.length > 1 ? 's were' : ' was'} already imported`;
        } else if (duplicateErrors.length > 0 && otherErrors.length > 0) {
          failureMessage = `${duplicateErrors.length} already imported, ${otherErrors.length} other error${otherErrors.length > 1 ? 's' : ''}`;
        } else if (otherErrors.length > 0) {
          failureMessage = `${otherErrors.length} failed to import`;
        }

        // Only show partial success notification if there are non-usage-limit failures
        if (nonUsageLimitFailures > 0) {
          notifications.show({
            title: 'Partial import success',
            message: `${successCount} recipe${successCount > 1 ? 's' : ''} started importing, ${failureMessage}.`,
            color: 'yellow',
            autoClose: 10000,
          });
        } else {
          // All failures were usage limits (already handled), show success for the ones that worked
          notifications.show({
            title: `${successCount} recipe import${successCount > 1 ? 's' : ''} started`,
            message: `Your recipe${successCount > 1 ? 's are' : ' is'} being processed. ${successCount > 1 ? 'They' : 'It'} will appear in your cookbook when ready.`,
            color: 'blue',
            icon: <IconCheck size={18} />,
            autoClose: 6000,
          });
        }

        // If some succeeded, trigger the callback
        if (successCount > 0) {
          onImportStarted();
        }

        // Keep modal open so user can see the status
        setState(prev => ({ ...prev, importing: false }));
      }

    } catch (error: any) {
      console.error('Unexpected recipe import error:', error);
      setState(prev => ({
        ...prev,
        importing: false,
        error: 'An unexpected error occurred during import.',
      }));

      notifications.show({
        title: 'Import error',
        message: 'An unexpected error occurred during import.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 10000,
      });
    }
  }, [state.activeTab, state.recipeGroups, state.urls, onClose, onImportStarted, handleReset, user]);


  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} size="lg">
          Import recipe{((state.activeTab === 'images' && state.recipeGroups.length > 1) ||
                        (state.activeTab === 'url' && state.urls.filter(u => u.trim()).length > 1)) ? 's' : ''}
        </Text>
      }
      size="lg"
      centered
      radius="lg"
      closeOnClickOutside={!state.importing}
      closeOnEscape={!state.importing}
      trapFocus={false}
    >
      <Stack gap="md">
        {/* Error Display */}
        {state.error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Import error"
            color="red"
            variant="light"
            radius="lg"
          >
            {state.error}
          </Alert>
        )}

        {/* Tabs for Import Method */}
        <Tabs value={state.activeTab} onChange={(tab) => setState(prev => ({ ...prev, activeTab: tab as 'images' | 'url' | 'pinterest' }))}>
          <Tabs.List grow>
            <Tabs.Tab value="images" leftSection={<IconFileUpload size={16} />}>
              <Box component="span" visibleFrom="xs">From </Box>Files
            </Tabs.Tab>
            <Tabs.Tab value="url" leftSection={<IconLink size={16} />}>
              <Box component="span" visibleFrom="xs">From </Box>URL
            </Tabs.Tab>
            <Tabs.Tab value="pinterest" leftSection={<IconBrandPinterest size={16} />}>
              <Box component="span" visibleFrom="xs">From </Box>Pinterest
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="images">
            <Stack gap="md" mt="md">
              {/* Recipe Groups */}
              {state.recipeGroups.map((group, index) => (
                <Stack key={group.id} gap="md">
                  {state.recipeGroups.length > 1 && (
                    <Group justify="space-between" align="center">
                      <Text fw={500} size="lg">
                        {['First', 'Second', 'Third', 'Fourth', 'Fifth'][index]} Recipe
                      </Text>
                      <Button
                        variant="subtle"
                        size="xs"
                        color="orange"
                        onClick={() => removeRecipeGroup(group.id)}
                        disabled={state.importing}
                      >
                        Remove
                      </Button>
                    </Group>
                  )}
                  
                  <ImageUpload
                    onImagesSelected={(images) => handleImagesSelected(group.id, images)}
                    onImagesCleared={() => handleImagesCleared(group.id)}
                    disabled={state.importing}
                    showTips={index === 0}
                  />
                </Stack>
              ))}

              {/* Add Another Recipe Button */}
              {state.recipeGroups.length < 5 && state.recipeGroups[0]?.images.length > 0 && (
                <>
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    onClick={addRecipeGroup}
                    disabled={state.importing}
                    fullWidth
                    hiddenFrom="sm"
                  >
                    Add another recipe
                  </Button>
                  
                  {/* Desktop version - centered */}
                  <Group justify="center" visibleFrom="sm">
                    <Button
                      variant="light"
                      leftSection={<IconPlus size={16} />}
                      onClick={addRecipeGroup}
                      disabled={state.importing}
                    >
                      Add another recipe
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="url">
            <Stack gap="md" mt="md">
              <Text size="sm" c="dimmed">
                Enter the URLs of recipes you'd like to import. We'll extract the recipe information automatically.
              </Text>
              
              {/* URL Inputs */}
              {state.urls.map((url, index) => (
                <Stack key={index} gap="sm">
                  <Group gap="sm" align="flex-end">
                    <>
                      {/* Desktop */}
                      <TextInput
                        placeholder="https://example.com/recipe"
                        value={url}
                        onChange={(e) => handleUrlChange(index, e.target.value)}
                        disabled={state.importing}
                        style={{ flex: 1 }}
                        label={state.urls.length > 1 ? `Recipe ${index + 1} URL` : 'Recipe URL'}
                        visibleFrom="sm"
                      />
                      {/* Mobile */}
                      <TextInput
                        placeholder="https://example.com/recipe"
                        value={url}
                        onChange={(e) => handleUrlChange(index, e.target.value)}
                        disabled={state.importing}
                        style={{ flex: 1 }}
                        label={state.urls.length > 1 ? `Recipe ${index + 1} URL` : 'Recipe URL'}
                        size="lg"
                        hiddenFrom="sm"
                      />
                    </>
                    {state.urls.length > 1 && (
                      <Button
                        variant="subtle"
                        size="xs"
                        color="orange"
                        onClick={() => removeUrl(index)}
                        disabled={state.importing}
                      >
                        Remove
                      </Button>
                    )}
                  </Group>
                </Stack>
              ))}

              {/* Add Another URL Button */}
              {state.urls.length < 5 && state.urls[0]?.trim() !== '' && (
                <>
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    onClick={addUrl}
                    disabled={state.importing}
                    fullWidth
                    hiddenFrom="sm"
                  >
                    Add another URL
                  </Button>
                  
                  {/* Desktop version - centered */}
                  <Group justify="center" visibleFrom="sm">
                    <Button
                      variant="light"
                      leftSection={<IconPlus size={16} />}
                      onClick={addUrl}
                      disabled={state.importing}
                    >
                      Add another URL
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="pinterest">
            {user && (
              <PinterestImport
                user={user}
                pinterestConnected={pinterestConnected}
                onImportStarted={onImportStarted}
                onClose={onClose}
                active={state.activeTab === 'pinterest'}
              />
            )}
          </Tabs.Panel>
        </Tabs>

        {/* Import Button */}
        {((state.activeTab === 'images' && state.recipeGroups.some(group => group.images.length > 0)) ||
          (state.activeTab === 'url' && state.urls.some(url => url.trim() !== ''))) && (
          <>
            <Divider />
            <Group justify="flex-end" mt="md">
              <Button
                onClick={handleStartImport}
                loading={state.importing}
                size="md"
                disabled={state.importing}
              >
                Import {state.activeTab === 'images' 
                  ? state.recipeGroups.filter(group => group.images.length > 0).length 
                  : state.urls.filter(url => url.trim() !== '').length} recipe{((state.activeTab === 'images' && state.recipeGroups.filter(group => group.images.length > 0).length > 1) ||
                  (state.activeTab === 'url' && state.urls.filter(url => url.trim() !== '').length > 1)) ? 's' : ''}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};
